import {useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Button, Collapse, Grid, Typography} from '@mui/material';

import {ReactComponent as ArrowIcon} from '../../../../../../assets/arrowIcon.svg';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {getMonthName} from '../../../../../../utils/dateUtils/getMonthName';
import {sortByYearMonthDesc} from '../../../../../../utils/dateUtils/sortEvents';
import {ProfileWorkExperience} from '../../../../../applicant/profile/types';

interface Props {
  workExperiences?: ProfileWorkExperience[];
}

export const WorkExperiences = ({workExperiences}: Props) => {
  const {t} = useTranslation();
  const [collapse, setCollapse] = useState<boolean>(true);
  const currentLanguage = useContext(LanguageContext);

  const sortedExperiences = useMemo(
    () => sortByYearMonthDesc<ProfileWorkExperience>(workExperiences || []),
    [workExperiences]
  );

  if (!workExperiences || workExperiences.length === 0) {
    return;
  }

  return (
    <Grid container direction='column'>
      <Button
        onClick={() => {
          setCollapse(!collapse);
        }}
        disableRipple
        variant='text'
        sx={{
          textTransform: 'none',
          display: 'flex',
          justifyContent: 'flex-start',
          width: 'fit-content',
          padding: '0',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        endIcon={
          <ArrowIcon
            fill={orangeColors[100]}
            style={{
              transform: collapse ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.3s',
              height: '20px',
              width: '20px',
            }}
          />
        }
      >
        <Typography variant='h3'>{t('companyApplicants.applicantsCard.cardDetails.allWorkExperiences')}</Typography>
      </Button>
      <Collapse in={collapse} orientation='vertical'>
        <Grid container flexDirection={'column'} mt={1}>
          {!sortedExperiences.length && (
            <Typography variant='body1' fontStyle={'italic'}>
              {t('companyApplicants.applicantsCard.cardDetails.emptyState.noWorkExperiences')}
            </Typography>
          )}
          {sortedExperiences.map((workExperience, idx) => (
            <Grid
              item
              mb={2}
              key={`${workExperience.id}-${idx}`}
              display={'flex'}
              sx={{flexDirection: {xs: 'column', sm: 'row'}}}
            >
              <Box>
                <Typography variant='h4'>{workExperience.roleName}</Typography>
                <Typography variant='body1'>{workExperience.organizationName}</Typography>
                <Typography variant='body1' color='primary.var70'>
                  {getMonthName(workExperience.startMonth, currentLanguage)} {workExperience.startYear} -{' '}
                  {workExperience.isCurrent
                    ? t('companyApplicants.applicantsCard.cardDetails.currentWorkExperience')
                    : `${getMonthName(workExperience.endMonth, currentLanguage)} ${workExperience.endYear}`}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Grid>
  );
};
